import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { useServerNow, type ServerNow } from '../../hooks/useServerNow';
import { Button } from '@repo/ui/src/components/ui/button';

// Dev-only time-travel control (guarded by import.meta.env.DEV at the mount
// site in DashboardLayout, and again below as a belt-and-suspenders check —
// this widget must never render, let alone bundle-visibly appear, in prod).
// Talks to POST /api/internal/dev/clock (devOnly + 404 in prod on the server
// side too), then invalidates every date-scoped query so the UI re-renders
// at the new simulated "now".

type DevClockBody = { at: string } | { advanceDays: number } | { advanceMinutes: number } | { reset: true };

const INVALIDATE_KEYS = ['now', 'sessions', 'slots', 'invoices', 'workbench', 'clients'] as const;

export function DevClockWidget() {
  if (!import.meta.env.DEV) return null;
  return <DevClockWidgetInner />;
}

function DevClockWidgetInner() {
  const now = useServerNow();
  const qc = useQueryClient();
  const [dateInput, setDateInput] = useState('');
  // Start collapsed so it never covers the right-column co-pilot chatbox; it
  // sits as a small bookmark tab peeking off the right edge until clicked.
  const [collapsed, setCollapsed] = useState(true);

  const setClock = useMutation({
    mutationFn: (body: DevClockBody) =>
      fetchApi<ServerNow>('/internal/dev/clock', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      for (const key of INVALIDATE_KEYS) qc.invalidateQueries({ queryKey: [key] });
    },
  });

  const today = now.toISOString().slice(0, 16).replace('T', ' '); // VN wall-clock date + time

  // Collapsed: a small bookmark tab flush to the right edge, lifted off the
  // bottom so it doesn't crowd the corner. Click to expand.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-24 right-0 z-50 rounded-l-md border border-r-0 bg-card px-2 py-2 text-sm shadow-lg hover:bg-accent"
        title={`Dev clock — ${today} (bấm để mở)`}
      >
        🕒
      </button>
    );
  }

  return (
    // bottom-20 on phones clears BottomTabBar (fixed, h-16 + safe-area, z-40);
    // md+ has no tab bar, so it collapses back to a snug bottom-3 there.
    <div className="fixed bottom-20 right-3 z-50 flex flex-col gap-1.5 rounded-md border bg-card px-3 py-2 text-xs shadow-lg md:bottom-3">
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        className="flex items-center gap-1.5 font-medium"
        title="Thu gọn"
      >
        <span>🕒 Dev clock</span>
        <span className="tabular-nums text-muted-foreground">{today}</span>
        <span className="ml-2 text-muted-foreground">▾</span>
      </button>
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={dateInput}
          onChange={(e) => setDateInput(e.target.value)}
          className="border rounded px-1.5 py-1 text-xs bg-background"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={!dateInput || setClock.isPending}
          onClick={() => setClock.mutate({ at: `${dateInput}T00:00:00Z` })}
        >
          Đặt ngày
        </Button>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={setClock.isPending}
          onClick={() => setClock.mutate({ advanceMinutes: 60 })}
        >
          +1 giờ
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={setClock.isPending}
          onClick={() => setClock.mutate({ advanceMinutes: 360 })}
        >
          +6 giờ
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={setClock.isPending}
          onClick={() => setClock.mutate({ advanceDays: 1 })}
        >
          +1 ngày
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={setClock.isPending}
          onClick={() => setClock.mutate({ advanceDays: 7 })}
        >
          +1 tuần
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs text-destructive"
          disabled={setClock.isPending}
          onClick={() => setClock.mutate({ reset: true })}
        >
          Thật lại
        </Button>
      </div>
    </div>
  );
}
